import { useEffect, useState } from 'react';
import { Loader2, Plus, Search } from 'lucide-react';
import { PressEditDialog } from '@generated/components/PressEditDialog';
import { getPressList, getPressCounts, setPressStatus, removePressOutlet, PRESS_STATUSES } from '@generated/server/press';
import { getStaffPass } from '@generated/hooks/staffPass';
import { PressControls, EmailLine, type Outlet, type Status } from '@generated/components/PressControls';
import { PressLetterPanel } from '@generated/components/PressLetterPanel';

/** Staff page: the Public Relations List — outlets that cover newspapers and publishing. */
export function PressList() {
  const [items, setItems] = useState<Outlet[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [more, setMore] = useState(false);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const [status, setStatus] = useState<Status | ''>('');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [tick, setTick] = useState(0);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [edit, setEdit] = useState<Outlet | 'new' | null>(null);
  const [notice, setNotice] = useState('');

  useEffect(() => { const t = setTimeout(() => setDq(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    let on = true;
    setRefetching(true); setError('');
    getPressList({ data: { pass: getStaffPass(), q: dq || undefined, status: status || undefined } })
      .then((r) => { if (on) { setItems(r?.items ?? []); setCursor(r?.cursor ?? null); } })
      .catch((e) => { console.error(e); if (on) setError('Could not load the list.'); })
      .finally(() => { if (on) { setLoading(false); setRefetching(false); } });
    getPressCounts({ data: { pass: getStaffPass() } })
      .then((rows) => on && setCounts(Object.fromEntries((rows ?? []).map((r) => [r.outreachStatus ?? '', r.n]))))
      .catch(console.error);
    return () => { on = false; };
  }, [dq, status, tick]);

  async function loadMore() {
    if (!cursor) return;
    setMore(true);
    try { const r = await getPressList({ data: { pass: getStaffPass(), cursor, loaded: items.length, q: dq || undefined, status: status || undefined } }); setItems((p) => [...p, ...(r?.items ?? [])]); setCursor(r?.cursor ?? null); }
    catch (e) { console.error(e); setError('Could not load more.'); } finally { setMore(false); }
  }

  async function changeStatus(o: Outlet, s: Status) {
    const prev = items;
    setItems((list) => list.map((x) => (x.id === o.id ? { ...x, outreachStatus: s, lastContacted: s === 'Not Contacted' ? x.lastContacted : new Date() } : x)));
    try { await setPressStatus({ data: { pass: getStaffPass(), id: o.id, status: s } }); setTick((t) => t + 1); }
    catch (e) { console.error(e); setItems(prev); setError(`Could not update ${o.name}.`); }
  }

  async function remove(o: Outlet) {
    setConfirmId(null);
    const prev = items;
    setItems((list) => list.filter((x) => x.id !== o.id));
    try { await removePressOutlet({ data: { pass: getStaffPass(), id: o.id } }); setTick((t) => t + 1); }
    catch (e) { console.error(e); setItems(prev); setError(`Could not delete ${o.name}.`); }
  }

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  // Rows sharing an outlet name (e.g. America's Newspapers' board) show as one listing with everyone inside.
  const groups: Outlet[][] = [];
  const at = new Map<string, number>();
  for (const o of items) { const k = o.name.trim().toLowerCase(); const i = at.get(k); if (i === undefined) { at.set(k, groups.length); groups.push([o]); } else groups[i].push(o); }
  return (
    <section className="space-y-6">
      <header className="border-b-4 border-double border-foreground pb-4">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Press desk</p>
        <h1 className="font-[family-name:var(--font-heading)] text-4xl font-bold">Public Relations List</h1>
        <p className="mt-1 text-sm text-muted-foreground">Publications, newsletters and organizations that cover newspapers and publishing{total ? ` · ${groups.length && !cursor ? `${groups.length} outlets, ` : ''}${total} contacts` : ''}.</p>
      </header>
      <PressLetterPanel />
      <div className="flex flex-wrap gap-2">
        {(['', ...PRESS_STATUSES] as const).map((s) => (
          <button key={s || 'all'} type="button" onClick={() => setStatus(s)} className={`min-h-10 border-2 border-foreground px-3 text-xs font-semibold uppercase ${status === s ? 'bg-foreground text-background' : 'bg-card hover:bg-muted'}`}>
            {s || 'All'}{s && counts[s] ? ` · ${counts[s]}` : ''}
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <label className="flex min-h-11 flex-1 items-center gap-2 border-2 border-foreground bg-card px-3">
          <Search className="h-4 w-4" aria-hidden /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search outlets" className="w-full bg-transparent outline-none" />
          {refetching && <Loader2 className="h-4 w-4 animate-spin" />}
        </label>
        <button type="button" onClick={() => setEdit('new')} className="inline-flex min-h-11 items-center justify-center gap-2 border-2 border-foreground bg-primary px-4 font-semibold text-primary-foreground"><Plus className="h-4 w-4" />Add outlet</button>
      </div>
      {notice && <p className="border-l-4 border-primary bg-muted px-3 py-2 text-sm">{notice}</p>}
      <PressEditDialog edit={edit} onClose={() => setEdit(null)} onSaved={(m) => { setEdit(null); setNotice(m); setTick((t) => t + 1); }} />
      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading ? <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-24 animate-pulse bg-muted" />)}</div> : (
        <ul className={`divide-y-2 divide-foreground/15 border-2 border-foreground bg-card ${refetching ? 'opacity-60' : ''}`}>
          {items.length === 0 && <li className="p-6 text-center text-sm text-muted-foreground">No outlets match.</li>}
          {groups.map((g) => g.length > 1 ? (
            <li key={g[0].id} className="p-4">
              <h3 className="text-lg font-bold">{g[0].name} <span className="text-sm font-semibold text-muted-foreground">· {g.length} people</span></h3>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{[g[0].type, g[0].region].filter(Boolean).join(' · ')}</p>
              {g[0].website?.url && <a className="text-sm underline" href={g[0].website.url} target="_blank" rel="noreferrer">{g[0].website.label ?? 'website'}</a>}
              {g[0].coverage && <p className="mt-1 text-sm">{g[0].coverage}</p>}
              <ul className="mt-3 divide-y divide-foreground/15 border-l-4 border-foreground/30 pl-3">
                {g.map((o) => (
                  <li key={o.id} className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1 text-sm">
                      <p className="font-semibold">{o.contactName ?? 'No name'}</p>
                      {o.contactTitle && <p className="text-xs text-muted-foreground">{o.contactTitle}</p>}
                      <p><EmailLine o={o} /></p>
                      {o.notes && <p className="text-xs text-muted-foreground">{o.notes}</p>}
                    </div>
                    <PressControls o={o} label={o.contactName ?? o.name} confirming={confirmId === o.id} onStatus={(s) => void changeStatus(o, s)} onEdit={() => setEdit(o)} onAskDelete={() => setConfirmId(o.id)} onDelete={() => void remove(o)} onKeep={() => setConfirmId(null)} />
                  </li>
                ))}
              </ul>
            </li>
          ) : g.map((o) => (
            <li key={o.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start">
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-bold">{o.name}</h3>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{[o.type, o.region].filter(Boolean).join(' · ')}</p>
                {(o.contactName || o.contactTitle) && <p className="mt-1 text-sm font-semibold">{[o.contactName, o.contactTitle].filter(Boolean).join(', ')}</p>}
                <p className="mt-1 break-words text-sm">
                  {o.email?.email ? <a className="underline" href={`mailto:${o.email.email}`}>{o.email.email}</a> : <span className="text-xs italic text-muted-foreground">No email yet</span>}
                  {o.website?.url && <a className="ml-3 underline" href={o.website.url} target="_blank" rel="noreferrer">{o.website.label ?? 'website'}</a>}
                </p>
                {o.coverage && <p className="mt-1 text-sm">{o.coverage}</p>}
                {o.notes && <p className="mt-1 text-sm text-muted-foreground">{o.notes}</p>}
                {o.lastContacted && <p className="mt-1 text-xs">Last contacted {o.lastContacted.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' })}</p>}
              </div>
              <PressControls o={o} label={o.name} confirming={confirmId === o.id} onStatus={(s) => void changeStatus(o, s)} onEdit={() => setEdit(o)} onAskDelete={() => setConfirmId(o.id)} onDelete={() => void remove(o)} onKeep={() => setConfirmId(null)} />
            </li>
          )))}
        </ul>
      )}
      {cursor && <button type="button" onClick={loadMore} disabled={more} className="mx-auto flex min-h-11 items-center gap-2 border-2 border-foreground px-5 font-semibold">{more && <Loader2 className="h-4 w-4 animate-spin" />}Load more</button>}
    </section>
  );
}
